import Alpine from 'alpinejs';
import Swiper from 'swiper';
import { Navigation } from 'swiper/modules';
import 'swiper/css';

// Components

// Global
window.Alpine = Alpine;

// Inits
const splitHeroTitleText = (node, letterIndex) => {
    if (node.nodeType === Node.TEXT_NODE) {
        const fragment = document.createDocumentFragment();
        const parts = node.textContent.split(/(\s+)/);

        parts.forEach((part) => {
            if (!part) {
                return;
            }

            if (/^\s+$/.test(part)) {
                fragment.appendChild(document.createTextNode(part));
                return;
            }

            const word = document.createElement('span');
            word.className = 'hero-title-reveal__word';
            word.setAttribute('aria-hidden', 'true');

            [...part].forEach((character) => {
                const mask = document.createElement('span');
                const letter = document.createElement('span');

                mask.className = 'hero-title-reveal__letter-mask';
                letter.className = 'hero-title-reveal__letter';
                letter.textContent = character;
                letter.style.setProperty('--hero-letter-index', letterIndex.value);
                letterIndex.value += 1;

                mask.appendChild(letter);
                word.appendChild(mask);
            });

            fragment.appendChild(word);
        });

        node.replaceWith(fragment);
        return;
    }

    [...node.childNodes].forEach((child) => splitHeroTitleText(child, letterIndex));
};

const initHeroTitleReveal = () => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return;
    }

    document.querySelectorAll('[data-hero-title-reveal]').forEach((title) => {
        if (title.dataset.heroTitleRevealReady === 'true') {
            return;
        }

        title.setAttribute('aria-label', title.textContent.replace(/\s+/g, ' ').trim());
        const letterIndex = { value: 0 };
        const description = title.parentElement?.querySelector('[data-hero-description-reveal]');

        splitHeroTitleText(title, letterIndex);
        title.dataset.heroTitleRevealReady = 'true';
        title.classList.add('hero-title-reveal');

        if (description) {
            description.style.setProperty('--hero-description-delay', `${Math.max(0, letterIndex.value - 1) * 24 + 950}ms`);
            description.classList.add('hero-description-reveal');
        }
    });
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initHeroTitleReveal, { once: true });
} else {
    initHeroTitleReveal();
}

document.querySelectorAll('[data-mega-menu]').forEach((menu) => {
    const openButtons = [...document.querySelectorAll(`[data-mega-menu-open][aria-controls="${menu.id}"]`)];
    const closeButtons = [...menu.querySelectorAll('[data-mega-menu-close]')];
    const serviceTriggers = [...menu.querySelectorAll('[data-mega-menu-service-trigger]')];
    const mobileToggles = [...menu.querySelectorAll('[data-mega-menu-mobile-toggle]')];
    const defaultPanel = menu.querySelector('[data-mega-menu-default-panel]');
    const servicePanels = [...menu.querySelectorAll('[data-mega-menu-service-panel]')];
    const inner = menu.querySelector('.mega-menu__inner');
    const desktopMenuQuery = window.matchMedia('(min-width: 1024px)');
    const transitionDuration = 220;
    let lastFocusedElement = null;
    let closeTimer = null;

    const setActiveServicePanel = (panelId = null) => {
        servicePanels.forEach((panel) => {
            panel.classList.toggle('is-active', panel.dataset.megaMenuServicePanel === panelId);
        });

        serviceTriggers.forEach((trigger) => {
            trigger.classList.toggle('is-panel-active', trigger.dataset.megaMenuServiceTrigger === panelId);
        });

        defaultPanel?.classList.toggle('is-active', !panelId);
    };

    const closeMobilePanels = () => {
        mobileToggles.forEach((toggle) => {
            const panel = document.getElementById(toggle.getAttribute('aria-controls'));

            toggle.setAttribute('aria-expanded', 'false');

            if (panel) {
                panel.hidden = true;
            }
        });
    };

    const setOpenState = (isOpen) => {
        window.clearTimeout(closeTimer);
        menu.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
        document.documentElement.classList.toggle('overflow-hidden', isOpen);

        openButtons.forEach((button) => {
            button.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        });

        if (isOpen) {
            lastFocusedElement = document.activeElement;
            menu.hidden = false;
            window.requestAnimationFrame(() => {
                menu.classList.add('is-open');
                menu.querySelector('[data-mega-menu-close]')?.focus();
            });
            return;
        }

        menu.classList.remove('is-open');
        closeTimer = window.setTimeout(() => {
            menu.hidden = true;
        }, transitionDuration);
        setActiveServicePanel();
        closeMobilePanels();
        lastFocusedElement?.focus?.();
    };

    openButtons.forEach((button) => {
        button.addEventListener('click', () => setOpenState(true));
    });

    closeButtons.forEach((button) => {
        button.addEventListener('click', () => setOpenState(false));
    });

    menu.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
            setOpenState(false);
        }
    });

    serviceTriggers.forEach((trigger) => {
        const activatePanel = () => {
            if (desktopMenuQuery.matches) {
                setActiveServicePanel(trigger.dataset.megaMenuServiceTrigger);
            }
        };

        trigger.addEventListener('pointerenter', activatePanel);
        trigger.addEventListener('focus', activatePanel);
    });

    mobileToggles.forEach((toggle) => {
        toggle.addEventListener('click', () => {
            const panel = document.getElementById(toggle.getAttribute('aria-controls'));
            const isOpen = toggle.getAttribute('aria-expanded') === 'true';

            if (desktopMenuQuery.matches) {
                setActiveServicePanel(toggle.dataset.megaMenuServiceTrigger);
                return;
            }

            closeMobilePanels();

            if (panel && !isOpen) {
                toggle.setAttribute('aria-expanded', 'true');
                panel.hidden = false;
            }
        });
    });

    inner?.addEventListener('pointerleave', () => {
        if (desktopMenuQuery.matches) {
            setActiveServicePanel();
        }
    });

    inner?.addEventListener('focusout', () => {
        window.requestAnimationFrame(() => {
            if (desktopMenuQuery.matches && !inner.contains(document.activeElement)) {
                setActiveServicePanel();
            }
        });
    });
});

const googleMapStyles = [
    {
        elementType: 'geometry',
        stylers: [{ saturation: -100 }, { lightness: 12 }],
    },
    {
        elementType: 'labels.icon',
        stylers: [{ visibility: 'off' }],
    },
    {
        elementType: 'labels.text.fill',
        stylers: [{ color: '#4a4a4a' }],
    },
    {
        elementType: 'labels.text.stroke',
        stylers: [{ color: '#f4f4f4' }],
    },
    {
        featureType: 'road',
        elementType: 'geometry',
        stylers: [{ color: '#d4d4d4' }],
    },
    {
        featureType: 'water',
        elementType: 'geometry',
        stylers: [{ color: '#c8c8c8' }],
    },
];

const googleMapMarkerSvg = `
    <svg width="52" height="83" viewBox="0 0 52 83" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M0.5 33.0508L0.5 19.1729C0.5 16.818 1.42882 14.5656 3.08203 12.9053L12.8535 3.0918C14.5065 1.43179 16.7541 0.5 19.0908 0.5L32.9092 0.5C35.252 0.5 37.4934 1.43166 39.1465 3.0918L48.918 12.9053C50.5711 14.5655 51.5 16.8241 51.5 19.1729L51.5 33.0508C51.4999 36.225 50.5509 39.9652 49.0098 43.9941C47.471 48.0168 45.3553 52.293 43.0508 56.5313C40.7465 60.769 38.2597 64.9574 35.9805 68.8076C33.7086 72.6454 31.6373 76.1554 30.1729 79.0254C28.7673 81.4673 27.3091 82.5 26 82.5C24.6908 82.5 23.2317 81.4676 21.8262 79.0254C20.3619 76.1556 18.2912 72.6461 16.0195 68.8086C13.7403 64.9583 11.2536 60.7692 8.94922 56.5313C6.64458 52.2928 4.52907 48.0159 2.99023 43.9932C1.44912 39.9643 0.50009 36.2249 0.5 33.0508Z" fill="#71B985" stroke="#71B985"/>
        <circle cx="26" cy="26.3936" r="12" fill="white"/>
    </svg>
`;
const googleMapMarkerUrl = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(googleMapMarkerSvg)}`;

let googleMapsPromise = null;

const loadGoogleMaps = (apiKey) => {
    if (window.google?.maps) {
        return Promise.resolve(window.google.maps);
    }

    if (!apiKey) {
        return Promise.reject(new Error('Google Maps API key is missing.'));
    }

    if (googleMapsPromise) {
        return googleMapsPromise;
    }

    googleMapsPromise = new Promise((resolve, reject) => {
        const callbackName = 'octagonFinanceGoogleMapsReady';
        const script = document.createElement('script');

        window[callbackName] = () => {
            resolve(window.google.maps);
            delete window[callbackName];
        };

        script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&callback=${callbackName}`;
        script.async = true;
        script.defer = true;
        script.onerror = () => reject(new Error('Google Maps failed to load.'));

        document.head.appendChild(script);
    });

    return googleMapsPromise;
};

const initGoogleMap = async (element) => {
    const apiKey = element.dataset.googleMapApiKey;
    const address = element.dataset.address;
    const title = element.dataset.title || 'Octagon Finance';
    const fallbackCenter = { lat: 49.18583, lng: -2.11 };

    try {
        const maps = await loadGoogleMaps(apiKey);
        const map = new maps.Map(element, {
            center: fallbackCenter,
            zoom: 16,
            disableDefaultUI: true,
            gestureHandling: 'cooperative',
            zoomControl: true,
            styles: googleMapStyles,
        });

        const placeMarker = (position) => {
            new maps.Marker({
                position,
                map,
                title,
                icon: {
                    url: googleMapMarkerUrl,
                    scaledSize: new maps.Size(52, 83),
                    anchor: new maps.Point(26, 83),
                },
            });
            map.setCenter(position);
        };

        if (!address) {
            placeMarker(fallbackCenter);
            return;
        }

        new maps.Geocoder().geocode({ address }, (results, status) => {
            if (status === 'OK' && results?.[0]) {
                placeMarker(results[0].geometry.location);
                return;
            }

            placeMarker(fallbackCenter);
        });
    } catch (error) {
        element.classList.add('is-unavailable');
        console.warn(error);
    }
};

document.querySelectorAll('[data-google-map]').forEach((element) => {
    initGoogleMap(element);
});

document.querySelectorAll('.js-featured-services-swiper').forEach((element) => {
    const section = element.closest('.featured-services');

    new Swiper(element, {
        modules: [Navigation],
        slidesPerView: 'auto',
        spaceBetween: 14,
        speed: 550,
        threshold: 6,
        watchOverflow: true,
        navigation: {
            prevEl: section?.querySelector('.featured-services__nav--prev'),
            nextEl: section?.querySelector('.featured-services__nav--next'),
        },
        breakpoints: {
            640: {
                spaceBetween: 16,
            },
            1024: {
                spaceBetween: 18,
            },
        },
    });
});

document.querySelectorAll('.js-advice-slider-swiper').forEach((element) => {
    const section = element.closest('.advice-slider');

    new Swiper(element, {
        modules: [Navigation],
        slidesPerView: 'auto',
        spaceBetween: 14,
        speed: 550,
        watchOverflow: true,
        navigation: {
            prevEl: section?.querySelector('.advice-slider__nav--prev'),
            nextEl: section?.querySelector('.advice-slider__nav--next'),
        },
        breakpoints: {
            640: {
                spaceBetween: 16,
            },
            1024: {
                spaceBetween: 24,
            },
        },
    });
});

document.querySelectorAll('.js-testimonial-slider-swiper').forEach((element) => {
    const section = element.closest('.testimonial-slider');

    new Swiper(element, {
        modules: [Navigation],
        slidesPerView: 'auto',
        spaceBetween: 14,
        speed: 550,
        watchOverflow: true,
        navigation: {
            prevEl: section?.querySelector('.testimonial-slider__nav--prev'),
            nextEl: section?.querySelector('.testimonial-slider__nav--next'),
        },
        breakpoints: {
            640: {
                spaceBetween: 16,
            },
            1024: {
                spaceBetween: 18,
                slidesOffsetAfter: 80,
            },
        },
    });
});

document.querySelectorAll('.js-featured-partners-swiper').forEach((element) => {
    const section = element.closest('.featured-partners');

    new Swiper(element, {
        modules: [Navigation],
        slidesPerView: 'auto',
        spaceBetween: 14,
        speed: 550,
        watchOverflow: true,
        navigation: {
            prevEl: section?.querySelector('.featured-partners__nav--prev'),
            nextEl: section?.querySelector('.featured-partners__nav--next'),
        },
        breakpoints: {
            640: {
                spaceBetween: 16,
            },
            1024: {
                spaceBetween: 18,
            },
        },
    });
});

const closeVideoModal = (modal) => {
    const video = modal?.querySelector('video');

    if (!modal || !video) {
        return;
    }

    video.pause();
    video.currentTime = 0;
    modal.hidden = true;
    modal.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('overflow-hidden');
};

document.querySelectorAll('[data-video-modal-open]').forEach((button) => {
    button.addEventListener('click', () => {
        const modal = document.getElementById(button.dataset.videoModalOpen);
        const video = modal?.querySelector('video');

        if (!modal || !video) {
            return;
        }

        modal.hidden = false;
        modal.setAttribute('aria-hidden', 'false');
        document.documentElement.classList.add('overflow-hidden');
        video.play();
    });
});

document.querySelectorAll('.video-modal').forEach((modal) => {
    modal.querySelectorAll('[data-video-modal-close]').forEach((button) => {
        button.addEventListener('click', () => closeVideoModal(modal));
    });
});

document.querySelectorAll('[data-all-advice]').forEach((section) => {
    const filters = [...section.querySelectorAll('[data-all-advice-filter]')];
    const items = [...section.querySelectorAll('[data-all-advice-item]')];
    const loadButton = section.querySelector('[data-all-advice-load]');
    let activeCategory = 'all';
    let visibleCount = 8;

    const getMatchingItems = () => items.filter((item) => {
        if (activeCategory === 'all') {
            return true;
        }

        return item.dataset.categories.split(',').includes(activeCategory);
    });

    const updateItems = () => {
        const matchingItems = getMatchingItems();

        items.forEach((item) => {
            item.classList.add('is-hidden');
            item.classList.remove('is-featured');
        });

        matchingItems.forEach((item, index) => {
            item.classList.toggle('is-hidden', index >= visibleCount);
            item.classList.toggle('is-featured', index < 2);
        });

        if (loadButton) {
            loadButton.hidden = visibleCount >= matchingItems.length;
        }
    };

    filters.forEach((filter) => {
        filter.addEventListener('click', () => {
            activeCategory = filter.dataset.allAdviceFilter;
            visibleCount = 8;

            filters.forEach((button) => {
                const isActive = button === filter;

                button.classList.toggle('is-active', isActive);
                button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
            });

            updateItems();
        });
    });

    loadButton?.addEventListener('click', () => {
        visibleCount += 3;
        updateItems();
    });

    updateItems();
});

document.querySelectorAll('[data-mortgage-calculator]').forEach((calculator) => {
    const pairs = [
        { key: 'price', step: 1000, prefix: '£' },
        { key: 'deposit', step: 1000, prefix: '£' },
        { key: 'rate', step: 0.01, suffix: '%' },
        { key: 'term', step: 1, suffix: 'years' },
    ];

    const outputs = {
        monthly: calculator.querySelector('[data-mortgage-output="monthly"]'),
        mortgage: calculator.querySelector('[data-mortgage-output="mortgage"]'),
        stamp: calculator.querySelector('[data-mortgage-output="stamp"]'),
        legal: calculator.querySelector('[data-mortgage-output="legal"]'),
        total: calculator.querySelector('[data-mortgage-output="total"]'),
    };

    const isRemortgage = calculator.dataset.remortgage === 'true';
    const firstTimeBuyer = calculator.querySelector('[data-mortgage-input="firstTimeBuyer"]');
    const quoteButton = calculator.querySelector('[data-mortgage-quote-button]');

    const formatNumber = (value, maximumFractionDigits = 0) => Number(value).toLocaleString('en-GB', {
        maximumFractionDigits,
        minimumFractionDigits: maximumFractionDigits,
    });

    const formatValue = (value, pair) => {
        const decimals = pair.step < 1 ? 2 : 0;
        const formatted = Number(value).toLocaleString('en-GB', {
            maximumFractionDigits: decimals,
            minimumFractionDigits: pair.step < 1 ? 2 : 0,
        });

        if (pair.prefix) {
            return `${pair.prefix}${formatted}`;
        }

        if (pair.suffix === 'years') {
            return `${formatted} ${Number(value) === 1 ? 'year' : 'years'}`;
        }

        if (pair.suffix) {
            return `${formatted}${pair.suffix}`;
        }

        return formatted;
    };

    const sanitise = (value) => parseFloat(String(value).replace(/[^\d.]/g, ''));
    const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
    const snap = (value, step) => (step >= 1 ? Math.round(value / step) * step : Number(value).toFixed(2));

    const calcLegal = (value) => Math.min(Math.max(value * 0.005, 1000), 5000);

    const stdStamp = (value) => {
        let total = 90;
        const bands = [
            { limit: 50000, rate: 0.005 },
            { limit: 300000, rate: 0.015 },
            { limit: 500000, rate: 0.02 },
            { limit: 700000, rate: 0.03 },
            { limit: 1000000, rate: 0.035 },
            { limit: 1500000, rate: 0.045 },
            { limit: 2000000, rate: 0.055 },
            { limit: 3000000, rate: 0.075 },
            { limit: 6000000, rate: 0.1 },
            { limit: Infinity, rate: 0.11 },
        ];
        let previous = 0;

        bands.some((band) => {
            if (value <= previous) {
                return true;
            }

            const slice = Math.min(value, band.limit) - previous;
            total += slice * band.rate;
            previous = band.limit;

            return value <= band.limit;
        });

        if (value <= 50000 && total - 90 < 10) {
            total = 100;
        }

        return total;
    };

    const ftbStamp = (value) => {
        if (value > 700000) {
            return stdStamp(value);
        }

        if (value <= 350000) {
            return 90;
        }

        if (value <= 600000) {
            return 90 + ((value - 350000) * 0.01);
        }

        const reduction = 8500 - ((value - 600000) * 0.085);

        return Math.max(stdStamp(value) - reduction, 90);
    };

    const update = () => {
        const price = Number(calculator.querySelector('[data-mortgage-range="price"]')?.value) || 0;
        const deposit = Number(calculator.querySelector('[data-mortgage-range="deposit"]')?.value) || 0;
        const rate = Number(calculator.querySelector('[data-mortgage-range="rate"]')?.value) || 0;
        const years = Number(calculator.querySelector('[data-mortgage-range="term"]')?.value) || 0;
        const loan = Math.max(price - deposit, 0);
        const months = years * 12;
        const monthlyRate = rate / 100 / 12;
        const monthlyPayment = months
            ? monthlyRate
                ? (loan * (monthlyRate * ((1 + monthlyRate) ** months))) / (((1 + monthlyRate) ** months) - 1)
                : loan / months
            : 0;
        const totalMortgage = monthlyPayment * months;
        const stamp = isRemortgage ? 0 : (firstTimeBuyer?.checked ? ftbStamp(price) : stdStamp(price));
        const legal = calcLegal(price);
        const total = totalMortgage + stamp + legal;

        outputs.monthly.textContent = `£${formatNumber(monthlyPayment, 2)}`;
        outputs.mortgage.textContent = `£${formatNumber(loan)}`;
        if (outputs.stamp) {
            outputs.stamp.textContent = `£${formatNumber(stamp)}`;
        }
        outputs.legal.textContent = `£${formatNumber(legal)}`;
        outputs.total.textContent = `£${formatNumber(total)}`;
    };

    pairs.forEach((pair) => {
        const text = calculator.querySelector(`[data-mortgage-input="${pair.key}"]`);
        const range = calculator.querySelector(`[data-mortgage-range="${pair.key}"]`);

        if (!text || !range) {
            return;
        }

        const reflect = (value, shouldUpdate = true) => {
            text.value = formatValue(value, pair);
            range.value = value;
            range.style.setProperty('--range-progress', `${((value - range.min) / (range.max - range.min)) * 100}%`);

            if (shouldUpdate) {
                update();
            }
        };

        const validateAndSync = () => {
            const min = Number(text.dataset.min);
            const max = Number(text.dataset.max);
            const value = sanitise(text.value);

            if (Number.isNaN(value)) {
                text.classList.add('is-error');
                return;
            }

            const snapped = clamp(Number(snap(value, pair.step)), min, max);
            text.classList.remove('is-error');
            reflect(snapped);
        };

        range.addEventListener('input', () => reflect(Number(range.value)));
        text.addEventListener('blur', validateAndSync);
        text.addEventListener('keydown', (event) => {
            if (event.key === 'Enter') {
                event.preventDefault();
                validateAndSync();
                text.blur();
            }
        });

        reflect(Number(range.value), false);
    });

    firstTimeBuyer?.addEventListener('change', update);
    quoteButton?.addEventListener('click', (event) => {
        const applyForm = document.getElementById('apply');
        const propertyValueField = applyForm?.querySelector('[name="fields[propertyValue]"]');
        const priceText = calculator.querySelector('[data-mortgage-input="price"]');
        const priceRange = calculator.querySelector('[data-mortgage-range="price"]');
        const pricePair = pairs.find((pair) => pair.key === 'price');
        const propertyValue = priceText?.value || (pricePair && priceRange ? formatValue(priceRange.value, pricePair) : '');

        if (!applyForm) {
            return;
        }

        event.preventDefault();

        if (propertyValueField && propertyValue) {
            propertyValueField.value = propertyValue;
            propertyValueField.dispatchEvent(new Event('input', { bubbles: true }));
            propertyValueField.dispatchEvent(new Event('change', { bubbles: true }));
        }

        applyForm.scrollIntoView({ behavior: 'smooth', block: 'start' });
        history.pushState(null, '', '#apply');
    });
    update();
});

document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') {
        return;
    }

    document.querySelectorAll('.video-modal:not([hidden])').forEach(closeVideoModal);
});

document.querySelectorAll('.collapsible-content__items').forEach((group) => {
    group.querySelectorAll('.collapsible-content__item').forEach((item) => {
        item.addEventListener('toggle', () => {
            if (!item.open) {
                return;
            }

            group.querySelectorAll('.collapsible-content__item[open]').forEach((openItem) => {
                if (openItem !== item) {
                    openItem.open = false;
                }
            });
        });
    });
});

Alpine.start();
